import React, { useEffect, useState } from 'react';
import { Card, Typography, Avatar, Tag, Spin, Alert } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined, ShopOutlined } from '@ant-design/icons';
import sellerService, { Seller } from '../services/sellerService';

const { Title, Text } = Typography;

interface SellerProfileProps {
  sellerId?: number;
  identifier?: string; // username or email for profile lookup
}

const SellerProfile: React.FC<SellerProfileProps> = ({ sellerId, identifier }) => {
  const [seller, setSeller] = useState<Seller | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setError(null);

        let sellerData: Seller;

        if (identifier) {
          // Fetch seller by username or email
          const response = await sellerService.getSellerProfile(identifier);
          sellerData = response.data.attributes;
        } else if (sellerId) {
          // Fetch seller by ID
          const response = await sellerService.getSellerById(sellerId);
          sellerData = response.data.attributes;
        } else {
          throw new Error('Either sellerId or identifier must be provided');
        }

        setSeller(sellerData);
      } catch (err: any) {
        console.error('Error fetching seller profile:', err);
        setError(err.response?.data?.message || 'Failed to load seller profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [sellerId, identifier]);

  if (loading) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '8px' }}>Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '16px' }}>
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  if (!seller) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Text type="secondary">No seller profile data available</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <Title level={5} style={{ margin: '0 0 16px 0', color: '#1890ff' }}>
        Seller Profile
      </Title>

      <Card size="small">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          <Avatar icon={<ShopOutlined />} style={{ backgroundColor: '#722ed1', marginRight: '8px' }} />
          <div>
            <Title level={5} style={{ margin: 0 }}>{seller.seller_name}</Title>
            <Tag color={seller.is_active ? 'green' : 'red'}>
              {seller.is_active ? 'Active' : 'Inactive'}
            </Tag>
          </div>
        </div>

        <div style={{ marginLeft: '40px' }}>
          <div style={{ marginBottom: '8px' }}>
            <UserOutlined style={{ marginRight: '8px', color: '#666' }} />
            <Text strong>Contact Person: </Text>
            <Text>{seller.contact_person}</Text>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <Text strong>Username: </Text>
            <Text>{seller.username}</Text>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <PhoneOutlined style={{ marginRight: '8px', color: '#666' }} />
            <Text>{seller.phone}</Text>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <MailOutlined style={{ marginRight: '8px', color: '#666' }} />
            <Text>{seller.email}</Text>
          </div>
          {seller.address && (
            <div>
              <EnvironmentOutlined style={{ marginRight: '8px', color: '#666' }} />
              <Text>{seller.address}</Text>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SellerProfile;
